import { HocuspocusProvider } from "@hocuspocus/provider";
import { getBoardTypes, LOCAL } from "@mn/shared";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { api } from "@/lib/api";

const collabUrl = () => `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/collab`;

export interface BoardHandle {
  doc: Y.Doc;
  provider: HocuspocusProvider;
  undo: Y.UndoManager;
  types: ReturnType<typeof getBoardTypes>;
  close: () => void;
}

export function openBoard(
  boardId: string,
  user: { id: string; name: string; image: string | null; color: string },
  opts: {
    readOnly: boolean;
    onAuthFailed?: (reason: string) => void;
    onStatus?: (s: string) => void;
    onSynced?: () => void;
    /** Endpoint that issues the collab token (visitors of a published link use the share endpoint). */
    tokenUrl?: string;
  },
): BoardHandle {
  const doc = new Y.Doc();
  const name = `board:${boardId}`;
  const idb = opts.readOnly ? null : new IndexeddbPersistence(name, doc);
  const provider = new HocuspocusProvider({
    url: collabUrl(),
    name,
    document: doc,
    token: async () => (await api<{ token: string }>(opts.tokenUrl ?? "/api/collab-token")).token,
    onAuthenticationFailed: ({ reason }) => opts.onAuthFailed?.(reason),
    onStatus: ({ status }) => opts.onStatus?.(status),
    onSynced: () => opts.onSynced?.(),
  });
  provider.setAwarenessField("user", user);
  const types = getBoardTypes(doc);
  const undo = new Y.UndoManager([types.cards, types.lines, types.meta], {
    trackedOrigins: new Set([LOCAL]),
    captureTimeout: 300,
  });
  return {
    doc,
    provider,
    undo,
    types,
    close: () => {
      provider.setAwarenessField("pointer", null);
      undo.destroy();
      provider.destroy();
      idb?.destroy();
      doc.destroy();
    },
  };
}

export const presenceColor = (id: string) => {
  const palette = ["#f4a361", "#e77051", "#55b2a6", "#82b29b", "#e9c56a", "#8aa9ff", "#c58af9", "#ff8ac2"];
  return palette[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length]!;
};
